import { ChartDataSets, ChartOptions, ChartTooltipItem, ChartData } from 'chart.js';

/*
    Colours and chart.js options for the DOOH Campaign Results dashboard.

    Held apart from the component for the same reason campaign-graphical-analytics holds its own:
    an options literal is three screens of configuration that says nothing about the campaign, and
    reading the component is easier without it.

    The palette is the design's — TMC yellow on white, dark grey ink — with one addition the design
    does not cover: the two panels that need several series at once (a line per channel, a bar per
    frequency rung) cannot be drawn in one brand colour. Those series palettes were checked against
    the accessibility gates rather than chosen by eye; see the comments on each.
*/

/** The brand's own pairing, as every other primary surface on the platform uses it. */
export const BRAND_YELLOW = '#fde150';

export const INK = {
    /** Axis labels and tick text. Never a series colour: a light hue is illegible as text. */
    muted: '#72767d',
    /** Hairline gridlines, one step off the chart's white surface. */
    grid: '#e8eaed',
    /** The chart surface itself. Doubles as the ring around a line's markers. */
    surface: '#ffffff',
    /** Bars and lines where there is only one series, and the ink the tiles use. */
    primary: '#2f3541'
};

/**
 * The fill for a chart with a single series — the dark-on-white bar with a brand-yellow hover that
 * the platform's other bar chart (shared/statistics-chart) already uses.
 *
 * Brand yellow is not used as a fill here: at 1.3:1 against white a yellow bar is below the 3:1
 * floor a meaningful graphic has to clear, so yellow stays the accent (hover, active toggle, the
 * inline bars in the audience tables, which sit beside their own printed value and percentage).
 */
export const SINGLE_SERIES_FILL = INK.primary;
export const SINGLE_SERIES_HOVER = BRAND_YELLOW;

/**
 * Identity colours, for the one-line-per-channel chart.
 *
 * Fixed order, assigned by position and never cycled: a channel keeps its colour when the filter
 * changes the number of lines on screen. Validated as a set on a white surface — worst adjacent
 * pair ΔE 9.1 under protanopia, 19.6 under normal vision — so adjacent series stay apart for a
 * colour-blind reader. Three of them sit below 3:1 against white, which is why this chart always
 * carries its legend and its hover tooltip rather than leaving colour to carry identity alone.
 */
export const SERIES_PALETTE = [
    '#2a78d6', // blue
    '#eb6834', // orange
    '#1baf7a', // aqua
    '#eda100', // yellow
    '#e87ba4', // magenta
    '#008300', // green
    '#4a3aa7', // violet
    '#e34948'  // red
];

/**
 * How many channels can be drawn as their own line before the rest are folded into one.
 *
 * Past the palette's eight slots the only way to add a series is to invent a ninth hue, which is
 * indistinguishable from one of the eight under colour blindness. Folding the tail is honest
 * because OTS and LTS are summable — the page's own footnote says so — so the folded line is a
 * real total rather than an average of unlike things.
 */
export const MAX_TREND_SERIES = SERIES_PALETTE.length;

/**
 * Magnitude colours, for the Reach N+ bars: 1+, 2+, 3+… is an ordered scale, not a set of
 * identities, so it takes one hue light-to-dark rather than a hue per rung.
 *
 * Five rungs — the ladder the design draws and the floor the backend guarantees — are sampled from
 * this ramp with a visible lightness step between each and the lightest still clear of the white
 * surface. A longer ladder (the provider may answer further) samples the same range more finely,
 * which closes those steps; the legend and the hover tooltip carry the rung in that case. Dropping
 * the extra rungs instead would hide figures the provider actually reported.
 */
const FREQUENCY_RAMP = [
    '#86b6ef', '#6da7ec', '#5598e7', '#3987e5', '#2a78d6',
    '#256abf', '#1c5cab', '#184f95', '#104281', '#0d366b'
];

/** Evenly spaced steps of the ramp, always ending on its darkest. */
export function frequencyColours(count: number): string[] {
    if (count <= 1) {
        return [FREQUENCY_RAMP[FREQUENCY_RAMP.length - 1]];
    }
    const colours: string[] = [];
    for (let index = 0; index < count; index++) {
        colours.push(FREQUENCY_RAMP[Math.round((index * (FREQUENCY_RAMP.length - 1)) / (count - 1))]);
    }
    return colours;
}

export function seriesColour(index: number): string {
    return SERIES_PALETTE[index % SERIES_PALETTE.length];
}

// ---- number formatting -------------------------------------------------------------------------

export function formatCount(value: number): string {
    return Math.round(value || 0).toLocaleString('en-US');
}

/** Axis ticks only: a full OTS figure is eight digits and crowds the axis off the panel. */
export function formatCompact(value: number): string {
    const magnitude = Math.abs(value || 0);
    if (magnitude >= 1000000) {
        return (value / 1000000).toFixed(magnitude >= 10000000 ? 0 : 1) + 'M';
    }
    if (magnitude >= 1000) {
        return (value / 1000).toFixed(magnitude >= 10000 ? 0 : 1) + 'K';
    }
    return formatCount(value);
}

export function formatPercent(value: number): string {
    return (value || 0).toFixed(1) + '%';
}

const FONT = '"Montserrat", sans-serif';

/**
 * One shared tooltip treatment, matching campaign-graphical-analytics'.
 *
 * `annotate` appends a second reading of the same bar — the Reach N+ bars are drawn as headcounts,
 * because a reader comparing channels needs their scale, but the share of that channel's audience
 * is the figure the panel is named after and belongs on the same line.
 */
function tooltip(
    format: (value: number) => string,
    withSeries: boolean,
    annotate?: (datasetIndex: number, index: number) => string
): any {
    return {
        enabled: true,
        mode: withSeries ? 'index' : 'nearest',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleFontSize: 13,
        bodyFontSize: 13,
        bodySpacing: 4,
        xPadding: 12,
        yPadding: 12,
        cornerRadius: 4,
        callbacks: {
            label: (item: ChartTooltipItem, data: ChartData) => {
                const dataset = data.datasets[item.datasetIndex];
                const extra = annotate ? annotate(item.datasetIndex, item.index) : '';
                const value = format(Number(item.yLabel)) + extra;
                return dataset.label ? dataset.label + ': ' + value : value;
            }
        }
    };
}

function axes(format: (value: number) => string, stepGrid: boolean): any {
    return {
        xAxes: [
            {
                // The categories are already named along the axis; a vertical rule behind each
                // only adds ink, so only the value axis keeps its hairlines.
                gridLines: { display: false, drawBorder: true, color: INK.grid },
                ticks: { fontSize: 12, fontFamily: FONT, fontColor: INK.muted, maxRotation: 0, autoSkip: true }
            }
        ],
        yAxes: [
            {
                gridLines: { display: stepGrid, drawBorder: false, color: INK.grid, zeroLineColor: INK.grid },
                ticks: {
                    beginAtZero: true,
                    fontSize: 12,
                    fontFamily: FONT,
                    fontColor: INK.muted,
                    callback: (value: number) => format(value)
                }
            }
        ]
    };
}

/** Bars: one series, no legend — the panel's own heading says what is plotted. */
export function barChartOptions(format: (value: number) => string): ChartOptions {
    return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        plugins: { datalabels: { display: false } },
        tooltips: tooltip(format, false),
        scales: axes(format, true)
    };
}

/** Grouped bars: the rung is carried by the legend as well as by the colour. */
export function groupedBarChartOptions(
    format: (value: number) => string,
    annotate?: (datasetIndex: number, index: number) => string
): ChartOptions {
    return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: 'bottom',
            labels: { boxWidth: 12, fontSize: 12, fontFamily: FONT, fontColor: INK.muted }
        },
        plugins: { datalabels: { display: false } },
        tooltips: tooltip(format, true, annotate),
        scales: axes(format, true)
    };
}

/** The over-time lines. Legend on the right, where the design puts it. */
export function lineChartOptions(format: (value: number) => string): ChartOptions {
    return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: true,
            position: 'right',
            labels: { boxWidth: 12, usePointStyle: true, fontSize: 12, fontFamily: FONT, fontColor: INK.muted }
        },
        plugins: { datalabels: { display: false } },
        tooltips: tooltip(format, true),
        // Hovering anywhere on the column reads every channel at that date, which is the comparison
        // the panel exists for — a line chart that only answers when the cursor is on a 4px dot
        // answers almost never.
        hover: { mode: 'index', intersect: false },
        scales: axes(format, true)
    };
}

/** A bar dataset in the brand's single-series treatment. */
export function barDataset(label: string, values: number[]): ChartDataSets {
    return {
        label,
        data: values,
        backgroundColor: SINGLE_SERIES_FILL,
        hoverBackgroundColor: SINGLE_SERIES_HOVER,
        // Capped rather than filled: a bar that takes the whole band leaves the chart no air. The
        // 2px floor keeps a zero visible on the baseline instead of vanishing, which matters here —
        // a zero on this page is a figure the provider did not report, not an empty panel.
        maxBarThickness: 24,
        minBarLength: 2
    };
}

/** One rung of the Reach N+ ladder, or one segment of a grouped bar. */
export function groupedBarDataset(label: string, values: number[], colour: string): ChartDataSets {
    return {
        label,
        data: values,
        backgroundColor: colour,
        hoverBackgroundColor: colour,
        maxBarThickness: 18,
        minBarLength: 2
    };
}

/** One channel's line over time. */
export function lineDataset(label: string, values: number[], colour: string): ChartDataSets {
    return {
        label,
        data: values,
        borderColor: colour,
        backgroundColor: colour,
        pointBackgroundColor: colour,
        // A 2px ring in the surface colour, so markers stay legible where lines cross.
        pointBorderColor: INK.surface,
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: 2,
        lineTension: 0,
        fill: false
    };
}
